import React from "react";
import * as styles from "@styles/ClientsPage.css";
import { Settings } from "lucide-react";
import type { EmitOnAction, StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { Ok } from "@ps-bridge/ok";
import type { Project } from "@ps-bridge/models/core/dataContracts";
import * as Option from "@utils/Option";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import EmptyView from "@view/EmptyView";

export type ProjectsPanelToolbarPhase = "empty" | "search";
export type ProjectsPanelEmptyResultPhase = "hidden" | "visible";

type ProjectsPanelProps = {
  projects: Project[];
  filteredProjects: Project[];
  activeProjectId: OptionTs<string>;
  activeClientId: OptionTs<string>;
  clientQuery: string;
  projectNameOptions: string[];
  projectEmptyText: string;
  toolbarPhase: ProjectsPanelToolbarPhase;
  emptyResultPhase: ProjectsPanelEmptyResultPhase;
  onClientQueryChange: StateUpdate<string>;
  onCreateProject: () => Ok;
  onNavigateProject: EmitOnAction<string>;
  onOpenProjectMenu: (payload: {
    event: React.MouseEvent<HTMLButtonElement>;
    project: Project;
  }) => Ok;
  runIO: (effect: IOts<Ok>) => Ok;
  formatTime: (value: string) => string;
};

const ProjectsPanel: React.FC<ProjectsPanelProps> = ({
  projects: _projects,
  filteredProjects,
  activeProjectId,
  activeClientId,
  clientQuery,
  projectNameOptions,
  projectEmptyText,
  toolbarPhase,
  emptyResultPhase,
  onClientQueryChange,
  onCreateProject,
  onNavigateProject,
  onOpenProjectMenu,
  runIO,
  formatTime,
}) => {
  const hasActiveClient = Option.fold(activeClientId, () => false, () => true);

  const toolbarView = {
    empty: (
      <div className={styles.emptyCreate}>
        <button className={styles.primaryButtonLarge} onClick={onCreateProject}>
          Create your first project +
        </button>
      </div>
    ),
    search: (
      <div className={styles.toolbar}>
        <input
          className={styles.searchInput}
          placeholder="Search projects"
          value={clientQuery}
          onChange={(event) => runIO(onClientQueryChange(event.target.value))}
          list="project-search-options"
        />
        <datalist id="project-search-options">
          {projectNameOptions.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      </div>
    ),
  } as const satisfies Record<ProjectsPanelToolbarPhase, React.ReactElement>;

  const emptyResultView = {
    hidden: <EmptyView />,
    visible: <p className={styles.emptyState}>{projectEmptyText}</p>,
  } as const satisfies Record<ProjectsPanelEmptyResultPhase, React.ReactElement>;

  return (
    <div className={styles.panelBody}>
      {toolbarView[toolbarPhase]}
      <div className={styles.list}>
        {filteredProjects.map((project) => (
          <div
            key={project.id}
            className={`${styles.card} ${
              Option.fold(activeProjectId, () => false, (value) => value === project.id)
                ? styles.cardActive
                : ""
            }`}
            role="button"
            tabIndex={0}
            onClick={() => {
              if (hasActiveClient) {
                runIO(onNavigateProject(project.id));
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                if (hasActiveClient) {
                  runIO(onNavigateProject(project.id));
                }
              }
            }}
          >
            <button
              className={styles.cardTitle}
              onClick={() => {
                if (hasActiveClient) {
                  runIO(onNavigateProject(project.id));
                }
              }}
            >
              {project.name}
            </button>
            <p className={styles.cardMeta}>
              Last edited {formatTime(project.last_updated_at)}
            </p>
            <div className={styles.cardActions}>
              <button
                className={styles.primaryGhost}
                onClick={() => {
                  if (hasActiveClient) {
                    runIO(onNavigateProject(project.id));
                  }
                }}
              >
                Open
              </button>
              <div className={styles.menuWrapper}>
                <button
                  className={styles.ghostButton}
                  onClick={(event) => onOpenProjectMenu({ event, project })}
                >
                  <Settings size={16} className={styles.actionIcon} />
                  Manage
                </button>
              </div>
            </div>
          </div>
        ))}
        {emptyResultView[emptyResultPhase]}
      </div>
    </div>
  );
};

export default ProjectsPanel;
